// Live OCR page-progress poller for the Exam + Model Solution panels.
//
// The project-level "Run OCR" / "Analyze" digitize calls are a single
// synchronous POST, so the panel can't learn how far OCR has got from the
// request itself. While that POST is in flight we poll GET /ocr_status (a tiny
// payload backed by the project's ocr_progress_* columns) so Kim sees "page X
// of Y" tick up. Errors are swallowed — progress is best-effort decoration over
// the authoritative POST result.

import { apiJson } from "../api/client";

export type OcrProgress = { page: number | null; total: number | null };

export type OcrStatus = OcrProgress & {
  state: "running" | "done" | "error" | null;
  kind: string | null;
  pages: number | null;
  error: string | null;
};

/** Poll a background OCR job until it reaches a terminal state. Calls
 * `onProgress` each tick (for the live "page X of Y" line) and resolves with the
 * final status once `state` is `done` or `error`. Poll failures are ignored and
 * retried — the job runs server-side regardless. Pass an `AbortSignal` to stop
 * polling (e.g. when the panel unmounts); the promise then resolves to `null`. */
export function pollOcrJob(
  projectId: number,
  onProgress: (s: OcrStatus) => void,
  intervalMs = 1000,
  signal?: AbortSignal,
): Promise<OcrStatus | null> {
  return new Promise((resolve) => {
    let stopped = false;
    const stop = () => {
      if (!stopped) {
        stopped = true;
        resolve(null);
      }
    };
    if (signal) {
      if (signal.aborted) return resolve(null);
      signal.addEventListener("abort", stop, { once: true });
    }
    async function tick() {
      if (stopped) return;
      let s: OcrStatus | null = null;
      try {
        s = await apiJson<OcrStatus>(`/api/projects/${projectId}/ocr_status`);
      } catch {
        /* best-effort: retry next tick */
      }
      if (stopped) return;
      if (s) {
        onProgress(s);
        if (s.state === "done" || s.state === "error") {
          stopped = true;
          resolve(s);
          return;
        }
      }
      if (!stopped) setTimeout(() => void tick(), intervalMs);
    }
    void tick();
  });
}

/** Poll a project's OCR progress every `intervalMs`. Returns a `stop()` to call
 * from the caller's `finally` once the OCR POST resolves. */
export function pollOcrProgress(
  projectId: number,
  onUpdate: (p: OcrProgress) => void,
  intervalMs = 1000,
): () => void {
  let stopped = false;
  let timer: ReturnType<typeof setTimeout> | null = null;

  async function tick() {
    if (stopped) return;
    try {
      const p = await apiJson<OcrProgress>(`/api/projects/${projectId}/ocr_status`);
      if (!stopped) onUpdate(p);
    } catch {
      /* best-effort: ignore a failed poll and try again next tick */
    }
    if (!stopped) timer = setTimeout(() => void tick(), intervalMs);
  }

  void tick();
  return () => {
    stopped = true;
    if (timer) clearTimeout(timer);
  };
}
